import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import SegmentedProgressBar from "../components/SegmentedProgressBar";
import FilePreviewModal from "../components/FilePreviewModal";
import ProfileEdit from "../components/ProfileEdit";
import { CheckCircle, AlertTriangle, X, ExternalLink, RefreshCw, Image, Trash2 } from "lucide-react";

const STATUS_LABELS = {};
const STATUS_CLASSES = {};

export default function MahasiswaDashboard() {
  const token = localStorage.getItem("access_token");
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [bimbingan, setBimbingan] = useState([]);
  const [dosenList, setDosenList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitMsg, setSubmitMsg] = useState({ type: "", text: "" });
  const [sheetsStatus, setSheetsStatus] = useState(false);
  const [syncModal, setSyncModal] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMsg, setSyncMsg] = useState({ type: "", text: "" });
  const [previewModal, setPreviewModal] = useState(null); // {blobUrl, fileName}
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({ dosen_id: "", tanggal: "", topik: "", komentar: "", file: null });

  useEffect(() => {
    fetchBimbingan();
    fetchSheetsStatus();
    // Fetch dosen list for pembimbing options
    if (user) {
      const p1 = user.pembimbing1_id;
      const p2 = user.pembimbing2_id;
      if (p1 || p2) {
        axios.get(`${API}/users/dosen`, { withCredentials: true })
          .then(r => {
            const filtered = r.data.filter(d => d.id === p1 || d.id === p2);
            setDosenList(filtered);
          })
          .catch(() => {});
      }
    }
  }, [user]);

  useEffect(() => {
    // Handle Google Sheets connected callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("sheets") === "connected") {
      setSheetsStatus(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchBimbingan = async () => {
    try {
      const { data } = await axios.get(`${API}/bimbingan`, {
      headers: {
      Authorization: `Bearer ${token}`
      }
    });
      setBimbingan(data);
    } catch (_) {}
  };

  const fetchSheetsStatus = async () => {
    try {
      await axios.get(`${API}/sheets/status`, {
      headers: {
      Authorization: `Bearer ${token}`
      }
      });
      setSheetsStatus(data.connected);
    } catch (_) {}
  };

  // Progress calculations — hitung semua bimbingan (tidak perlu disetujui)
  const p1Id = user?.pembimbing1_id;
  const p2Id = user?.pembimbing2_id;
  const p1Count = bimbingan.filter(b => b.dosen_id === p1Id).length;
  const p2Count = bimbingan.filter(b => b.dosen_id === p2Id).length;
  const p1Name = dosenList.find(d => d.id === p1Id)?.name || "Pembimbing 1";
  const p2Name = dosenList.find(d => d.id === p2Id)?.name || "Pembimbing 2";
  const siapUjian = p1Count >= 8 && p2Count >= 8;

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files?.[0];
      if (file) {
        if (!["image/jpeg", "image/jpg"].includes(file.type)) {
          alert("File harus bertipe JPEG/JPG");
          e.target.value = "";
          return;
        }
        if (file.size > 1024 * 1024) {
          alert("Ukuran file maksimal 1 MB");
          e.target.value = "";
          return;
        }
        setForm(p => ({ ...p, file }));
      }
    } else {
      setForm(p => ({ ...p, [name]: value }));
    }
  };

  const resetForm = () => {
    setForm({ dosen_id: "", tanggal: "", topik: "", komentar: "", file: null });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.dosen_id || !form.tanggal || !form.topik || !form.komentar) {
      setSubmitMsg({ type: "error", text: "Semua field wajib diisi" });
      return;
    }
    setLoading(true);
    setSubmitMsg({ type: "", text: "" });
    try {
      const fd = new FormData();
      fd.append("dosen_id", form.dosen_id);
      fd.append("tanggal", form.tanggal);
      fd.append("topik", form.topik);
      fd.append("komentar", form.komentar);
      if (form.file) fd.append("file", form.file);

      await axios.post(`${API}/bimbingan`, fd, {headers: {Authorization: `Bearer ${token}`}});
      setSubmitMsg({ type: "success", text: "Bimbingan berhasil disimpan!" });
      resetForm();
      fetchBimbingan();
      setTimeout(() => setActiveTab("history"), 1500);
    } catch (err) {
      setSubmitMsg({ type: "error", text: err.response?.data?.detail || "Gagal menyimpan bimbingan" });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewFile = (fileUrl, fileName) => {
  setPreviewModal({
    blobUrl: fileUrl,
    fileName
    });
  };

  const closePreview = () => {
    if (previewModal?.blobUrl) URL.revokeObjectURL(previewModal.blobUrl);
    setPreviewModal(null);
  };

  const handleConnectSheets = async () => {
  try {

    const { data } = await axios.get(
      `${API}/sheets/connect`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    window.location.href = data.url;

  } catch (err) {
    console.error(err);
    alert("Gagal menghubungkan Google Sheets");
  }
};
  const handleSync = async () => {
    if (!spreadsheetId.trim()) { setSyncMsg({ type: "error", text: "Masukkan Spreadsheet ID" }); return; }
    setSyncLoading(true);
    setSyncMsg({ type: "", text: "" });
    try {
      await axios.post(`${API}/sheets/sync`,{ spreadsheet_id: spreadsheetId },{headers: {Authorization: `Bearer ${token}`}});
      setSyncMsg({ type: "success", text: data.message });
      fetchBimbingan();
    } catch (err) {
      setSyncMsg({ type: "error", text: err.response?.data?.detail || "Gagal sinkronisasi" });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDeleteBimbingan = async (id) => {
  const confirmDelete = window.confirm(
    "Yakin ingin menghapus catatan bimbingan ini?"
  );

  if (!confirmDelete) return;

  try {
    await axios.delete(`${API}/bimbingan/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    setBimbingan((prev) => prev.filter((b) => b.id !== id));

    alert("Catatan bimbingan berhasil dihapus");
  } catch (err) {
    alert(
      err.response?.data?.detail ||
      "Gagal menghapus catatan bimbingan"
    );
  }
};

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex font-[Manrope]">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />

      <main className="ml-64 flex-1 p-8">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Dashboard</h1>
              <p className="text-[#475569] mt-1">Pantau progress tugas akhir Anda</p>
            </div>

            {/* Profile + Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm col-span-2">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#475569] mb-3">Data Mahasiswa</h2>
                <div className="space-y-3">

                  <div className="grid grid-cols-[100px_1fr] gap-3">
                    <span className="text-sm font-semibold text-[#0F172A]">Nama</span>
                    <span className="text-sm text-[#475569]">{user?.name}</span>
                  </div>

                  <div className="grid grid-cols-[100px_1fr] gap-3">
                    <span className="text-sm font-semibold text-[#0F172A]">NIM</span>
                    <span className="text-sm text-[#475569]">{user?.nim || "-"}</span>
                  </div>

                  <div className="grid grid-cols-[100px_1fr] gap-3">
                    <span className="text-sm font-semibold text-[#0F172A]">Judul TA</span>
                    <span className="text-sm text-[#475569] leading-relaxed">
                      {user?.judul_ta || "-"}
                    </span>
                  </div>

                  <div className="grid grid-cols-[100px_1fr] gap-3">
                    <span className="text-sm font-semibold text-[#0F172A]">Email</span>
                    <span className="text-sm text-[#475569]">{user?.email}</span>
                  </div>

                </div>
              </div>

              <div className={`border rounded-sm p-5 shadow-sm flex flex-col items-center justify-center gap-2 ${siapUjian ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
                {siapUjian ? <CheckCircle size={32} className="text-green-600" /> : <AlertTriangle size={32} className="text-yellow-600" />}
                <span data-testid="status-ujian" className={`text-sm font-bold text-center ${siapUjian ? "text-green-700" : "text-yellow-700"}`}>
                  {siapUjian ? "SIAP UJIAN" : "BELUM MEMENUHI SYARAT"}
                </span>
                <p className="text-xs text-center text-[#475569]">
                  {siapUjian ? "Selamat! Anda memenuhi syarat sidang TA" : "Selesaikan minimal 8 sesi per pembimbing"}
                </p>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <h2 className="text-xs font-bold uppercase tracking-widest text-[#475569] mb-5">Progress Bimbingan</h2>
              <div className="space-y-5">
                <SegmentedProgressBar value={p1Count} label={p1Name} sublabel="Pembimbing 1" />
                <SegmentedProgressBar value={p2Count} label={p2Name} sublabel="Pembimbing 2" />
              </div>
            </div>

            {/* Stats - DIHAPUS */}

          </div>
        )}

        {/* Input Bimbingan */}
        {activeTab === "input" && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Input Bimbingan</h1>
              <p className="text-[#475569] mt-1">Catat sesi bimbingan tugas akhir Anda</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Pembimbing</label>
                    <select name="dosen_id" value={form.dosen_id} onChange={handleChange} data-testid="bimbingan-pembimbing"
                      className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]">
                      <option value="">Pilih Pembimbing</option>
                      {dosenList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Tanggal</label>
                    <input type="date" name="tanggal" value={form.tanggal} onChange={handleChange} data-testid="bimbingan-tanggal"
                      className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Topik Bahasan</label>
                  <input type="text" name="topik" value={form.topik} onChange={handleChange} placeholder="Topik yang dibahas" data-testid="bimbingan-topik"
                    className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]" />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Komentar / Catatan</label>
                  <textarea name="komentar" value={form.komentar} onChange={handleChange} placeholder="Catatan bimbingan..." rows={4} data-testid="bimbingan-komentar"
                    className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147] resize-none" />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Bukti Foto (JPEG, maks 1MB)</label>
                  <div className="flex items-center gap-3">
                    <input ref={fileInputRef} type="file" name="file" accept=".jpg,.jpeg,image/jpeg" onChange={handleChange} data-testid="bimbingan-file"
                      className="flex-1 border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none" />
                    {form.file && (
                      <button type="button" onClick={() => { setForm(p => ({ ...p, file: null })); fileInputRef.current.value = ""; }}
                        className="text-red-500 hover:text-red-700">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {submitMsg.text && (
                  <div data-testid="bimbingan-msg" className={`p-3 rounded-sm text-sm ${submitMsg.type === "success" ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
                    {submitMsg.text}
                  </div>
                )}

                <button type="submit" disabled={loading} data-testid="bimbingan-submit"
                  className="bg-[#002147] hover:bg-[#001833] disabled:bg-slate-400 text-white px-6 py-3 rounded-sm font-semibold text-sm transition-all duration-200 active:translate-y-px">
                  {loading ? "Menyimpan..." : "Simpan Bimbingan"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* History */}
        {activeTab === "history" && (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Riwayat Bimbingan</h1>
                <p className="text-[#475569] mt-1">{bimbingan.length} catatan bimbingan</p>
              </div>
              <div className="flex gap-2">
                {sheetsStatus ? (
                  <button onClick={() => setSyncModal(true)} data-testid="sync-sheets-btn"
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-sm text-sm font-semibold transition-all">
                    <RefreshCw size={14} />
                    Sync ke Google Sheets
                  </button>
                ) : (
                  <button onClick={handleConnectSheets} data-testid="connect-sheets-btn"
                    className="flex items-center gap-2 border border-[#002147] text-[#002147] hover:bg-[#002147] hover:text-white px-4 py-2 rounded-sm text-sm font-semibold transition-all">
                    <ExternalLink size={14} />
                    Hubungkan Google Sheets
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bimbingan-table">
                  <thead>
                    <tr className="bg-[#002147] text-white text-xs uppercase tracking-widest">
                      <th className="px-4 py-3 text-left font-bold">No</th>
                      <th className="px-4 py-3 text-left font-bold">Pembimbing</th>
                      <th className="px-4 py-3 text-left font-bold">Tanggal</th>
                      <th className="px-4 py-3 text-left font-bold">Topik</th>
                      <th className="px-4 py-3 text-left font-bold">Komentar</th>
                      <th className="px-4 py-3 text-left font-bold">Bukti Foto</th>
                      <th className="px-4 py-3 text-left font-bold">Bukti Foto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bimbingan.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-[#475569] text-sm">Belum ada data bimbingan</td></tr>
                    ) : bimbingan.map((b, i) => (
                      <tr key={b.id} data-testid={`bimbingan-row-${b.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-[#475569]">{i + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{b.dosen_name || "-"}</td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{b.tanggal}</td>
                        <td className="px-4 py-3 text-sm text-[#0F172A] max-w-xs break-words whitespace-pre-wrap">{b.topik}</td>
                        <td className="px-4 py-3 text-sm text-[#475569] max-w-xs break-words whitespace-pre-wrap">{b.komentar}</td>
                        <td className="px-4 py-3">
                          {b.file_name ? (
                            <button
                              onClick={() => handlePreviewFile(b.file_url, b.file_name)}
                              data-testid={`preview-file-btn-${b.id}`}
                              className="flex items-center gap-1 text-xs text-[#002147] hover:underline font-medium"
                            >
                              <Image size={12} /> Lihat Foto
                            </button>
                          ) : <span className="text-xs text-slate-400">-</span>}
                        </td>
                        <td className="px-4 py-3">
                              <button
                                onClick={() => handleDeleteBimbingan(b.id)}
                                className="flex items-center gap-1 text-red-600 hover:text-red-800 text-xs font-medium"
                                >
                              <Trash2 size={14} />
                                  Hapus
                             </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Profil */}
        {activeTab === "profil" && (
          <ProfileEdit />
        )}
      </main>

      {/* Sync Modal */}
      {syncModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0F172A] font-[Outfit]">Sync ke Google Sheets</h3>
              <button onClick={() => { setSyncModal(false); setSyncMsg({ type: "", text: "" }); }}><X size={18} className="text-[#475569]" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-4">Masukkan ID Google Spreadsheet tujuan. ID ada di URL spreadsheet Anda.</p>
            <input type="text" value={spreadsheetId} onChange={e => setSpreadsheetId(e.target.value)}
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
              data-testid="spreadsheet-id-input"
              className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147] mb-3" />
            {syncMsg.text && (
              <div className={`p-3 rounded-sm text-sm mb-3 ${syncMsg.type === "success" ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
                {syncMsg.text}
              </div>
            )}
            <button onClick={handleSync} disabled={syncLoading} data-testid="sync-confirm-btn"
              className="w-full bg-[#002147] hover:bg-[#001833] disabled:bg-slate-400 text-white py-2.5 rounded-sm font-semibold text-sm transition-all">
              {syncLoading ? "Menyinkronkan..." : "Sinkronisasi Sekarang"}
            </button>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewModal && (
        <FilePreviewModal
          blobUrl={previewModal.blobUrl}
          fileName={previewModal.fileName}
          onClose={closePreview}
        />
      )}
    </div>
  );
}
